<script setup lang="ts">
import { defineProps, onBeforeUnmount, onMounted, ref, TransitionGroup, withDefaults } from "vue";
import anime from "animejs";

// Props definition with withDefaults
const props = withDefaults(
  defineProps<{
    tag?: string;
    intersect?: boolean;
  }>(),
  {
    tag: 'div',
    intersect: false,
  }
);

// Reactive variables
const group = ref<InstanceType<typeof TransitionGroup> | null>(null); 
const observer = ref<IntersectionObserver | null>(null);
const intersected = ref(false);

// Methods
function onIntersect(entries: IntersectionObserverEntry[]) {
  const [entry] = entries;
  if (entry.intersectionRatio > 0) {
    observer.value?.unobserve(entry.target);
    intersected.value = true;
  }
}

function enter(el: HTMLElement, done: () => void) {
  const delay = Number(el.dataset.index) * 0.04 || 0;

  anime({
    targets: el,
    opacity: [0, 1],
    translateY: [-10, 0],
    duration: 600,
    easing: 'easeOutQuad',
    delay,
    complete: () => done(),
  });
}

function leave(el: HTMLElement, done: () => void) {
  anime({
    targets: el,
    opacity: [1, 0],
    duration: 200,
    easing: 'easeInQuad',
    complete: () => done(),
  });
}

// Lifecycle hooks
onMounted(() => {
  if (props.intersect && group.value?.$el instanceof HTMLElement) {
    observer.value = new IntersectionObserver(onIntersect);
    observer.value.observe(group.value.$el);
  }
});

onBeforeUnmount(() => {
  observer.value?.disconnect();
});
</script>

<template>
  <transition-group
    ref="group"
    :tag="tag"
    class="transition-group-fade"
    name="group-fade"
    appear
    @enter="enter"
    @leave="leave">
    <slot v-if="!intersect || (intersect && intersected)" />
  </transition-group>
</template>

<style lang="scss" scoped>
.transition-group-fade {
  /* Ensure deep selector styling for scoped transition effects */
  :deep( > * ) {
    transition: transform 0.8s;
  }
}
</style>
